import React, { useState } from 'react';
import { Mail, Shield, Check } from 'lucide-react';
import { useHideNavOnModal } from '../hooks/useHideNavOnModal';

const EmailVerifyForLoggedUser = ({ isOpen, emailMasked, onSuccess, api }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Esconde nav quando modal está aberto
  useHideNavOnModal(isOpen);

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      setError('Digite o código de 6 dígitos');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await api.post('/api/profile/email/verify', { code });
      
      onSuccess();
    } catch (error) {
      setError(error.response?.data?.detail || 'Código incorreto');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeInput = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-[9999] backdrop-blur-md modal-anim">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border-2 border-green-600">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-green-600 via-emerald-600 to-green-600 px-8 py-6">
          <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-emerald-400/20"></div>
          <div className="relative text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail size={32} className="text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-2xl font-black text-white mb-1">
              Verifique seu Email
            </h2>
            <p className="text-green-100 text-sm font-medium">Um código foi enviado para {emailMasked}</p>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* Info */}
          <div className="bg-green-900/30 border-2 border-green-700 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Shield className="text-green-400 flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-bold text-white text-lg mb-1">Verificação de Segurança</h3>
                <p className="text-sm text-green-200">
                  Digite o código de 6 dígitos enviado para seu email para continuar usando o sistema.
                </p>
              </div>
            </div>
          </div>

          {/* Code Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-3">
                Código de Verificação
              </label>
              <input
                type="text"
                value={code}
                onChange={handleCodeInput}
                placeholder="000000"
                className="w-full p-4 bg-gray-800 text-white text-center text-2xl font-bold tracking-widest border-2 border-gray-600 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                maxLength={6}
                autoFocus
              />
              <p className="text-xs text-gray-400 mt-2 text-center">
                Digite os 6 números recebidos no email
              </p>
            </div>

            {error && (
              <div className="bg-red-900/50 border-2 border-red-500 text-red-200 rounded-lg p-3 text-sm font-semibold text-center">
                {error}
              </div>
            )}
          </div>

          {/* Verify Button */}
          <button
            onClick={handleVerify}
            disabled={loading || code.length !== 6}
            className="w-full px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none text-lg flex items-center justify-center gap-2"
          >
            {loading ? (
              'Verificando...'
            ) : (
              <>
                <Check size={20} />
                Verificar Código
              </>
            )}
          </button>

          {/* Warning */}
          <div className="bg-yellow-900/30 border border-yellow-700 rounded-xl p-4">
            <p className="text-xs text-yellow-200 text-center font-semibold">
              ⚠️ Não recebeu o código? Verifique sua caixa de spam ou aguarde alguns minutos
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerifyForLoggedUser;
